import React, { FC, useState, useEffect, useRef } from 'react'
import styled from 'styled-components'
import {
    Dialog,
    IconButton,
    Button,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Switch,
    Checkbox,
} from '@material-ui/core'
import { useTranslation } from 'react-i18next'
import { Close, ExpandMore } from '@material-ui/icons'
import { theme, Text, Space, Icon } from 'ui'
import { observer } from 'mobx-react'
import useStore, { snackBar } from 'store'
import Autocomplete from '@material-ui/lab/Autocomplete'
import TextField from '@material-ui/core/TextField'
import icons from 'components/Groups/groupCategories.json'
import TextareaAutosize from '@material-ui/core/TextareaAutosize'
import type { ModuleType } from 'utils/types'
import { ModuleEnum, ProviderEnum, SERVICE_DESK_URL_OPTIONS } from 'utils/types'
import { validURL } from 'utils/validations'
import RichTextEditor from "../../../components/Messages/RichTextEditor";
import { isTotzeret } from 'service/whiteLable'
import kehilanetData from '../../../data/kehilanet.json';
import { userIsSuperAdmin } from 'utils/roles'


const DialogContainer = styled(Dialog)`
  .MuiDialog-paper {
    border-radius: 16px;
    overflow: hidden;
    max-width: 96vw;
  }
`
const HeaderBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 64px;
  flex-shrink: 0;
  padding: 0 24px;
  background: ${theme.color.white};
  border-bottom: 1px solid ${theme.color.lightGrayBackground};

  .header-title {
    font-size: 20px;
    font-weight: 700;
    color: ${theme.color.mekomiBlack};
    margin: 0;
  }
  .MuiIconButton-root {
    padding: 8px;
    color: ${theme.color.darkGray};
    border-radius: 10px;
    transition: ${theme.css.transition};
  }
  .MuiIconButton-root:hover {
    background: ${theme.color.dialogBackground};
    color: ${theme.color.base};
  }
`
const FooterBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-shrink: 0;
  padding: 14px 24px;
  background: ${theme.color.white};
  border-top: 1px solid ${theme.color.lightGrayBackground};
`
const StyledCheckBox = styled(Checkbox)`
  &.MuiCheckbox-root {
    color: ${theme.color.confirmButton};
    padding: 0 8px 0 0;
  }
`
const CheckBoxContainer = styled.div`
    display: flex;
    flex-direction: row-reverse;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    margin: 8px 0;
    padding: 11px 16px;
    border: 1px solid ${theme.color.lightGrayBackground};
    border-radius: 10px;
    background: ${theme.color.whiteBackground};
`
const ContainerDialog = styled.div`
    display: flex;
    flex-direction: column;
    background-color: ${theme.color.dialogBackground};
    padding: 20px;
    overflow: auto;
    max-height: 66vh;
    width: 640px;
    max-width: 92vw;
`
const Card = styled.div`
    display: flex;
    flex-direction: column;
    align-items: stretch;
    background: ${theme.color.white};
    border: 1px solid ${theme.color.lightGrayBackground};
    border-radius: 14px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
    padding: 22px 24px;
`
const SelectContent = styled.div`
    align-items:center;
    flex-direction:row;
    display:flex;

`
const ConfirmButton = styled(Button)`
  &.MuiButtonBase-root {
    font-size: 15px;
    font-weight: 600;
    text-transform: none;
    min-width: 132px;
    height: 44px;
    border-radius: 10px;
    background: ${theme.color.confirmButton};
    box-shadow: 0 4px 12px ${theme.color.confirmButton}33;
    transition: ${theme.css.transition};
    &:hover {
      background: ${theme.color.darkBlue};
      box-shadow: 0 6px 16px ${theme.color.confirmButton}4d;
    }
    color: ${theme.color.white};
    &.Mui-disabled {
      opacity: 0.4;
      box-shadow: none;
    }
  }
`
const DeleteButton = styled(Button)`
  &.MuiButtonBase-root {
    font-size: 15px;
    font-weight: 600;
    text-transform: none;
    min-width: 120px;
    height: 44px;
    border-radius: 10px;
    background: transparent;
    border: 1.5px solid ${theme.color.red}66;
    transition: ${theme.css.transition};
    &:hover {
      background: ${theme.color.red}12;
      border-color: ${theme.color.red};
    }
    color: ${theme.color.red};
    &.Mui-disabled {
      opacity: 0.4;
    }
  }
`
const IconsContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  cursor:pointer;
`
const StyledTextField = styled(TextField)`
  width: 100%;
  .MuiOutlinedInput-root {
    border-radius: 8px;
    background: ${theme.color.white};
  }
  .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline {
    border-color: ${theme.color.confirmButton}80;
  }
  .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
    border-color: ${theme.color.confirmButton};
  }
`
const StyledTextarea = styled(TextareaAutosize)`
  width: 100%;
  min-height: 84px;
  padding: 12px 14px;
  border-radius: 8px;
  border: 1px solid ${theme.color.inputGrayBorder};
  font-family: inherit;
  font-size: 15px;
  line-height: 1.5;
  color: ${theme.color.black};
  resize: vertical;
  outline: none;
  transition: ${theme.css.transition};
  &:hover {
    border-color: ${theme.color.confirmButton}80;
  }
  &:focus {
    border-color: ${theme.color.confirmButton};
  }
`
const IconBg = styled.div<{ selected: boolean }>`
  background: ${p => isTotzeret ? 'transparent' : p.color};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem 0.33rem;
  border-radius: 8px;
  /* @noflip */ margin-right:35px;
  border-color: ${p => p.selected ? theme.color.select : theme.color.white};
  border-width: 4px;
  border-style: solid;
  body[dir=rtl] & {
    margin-right: unset;
    margin-left: 20px;
    }
`

enum ValidFields {
    information = 'information',
    shareEmail = 'shareEmail'
}

enum ValidLengthFields {
    information = 2000,
    shareEmail = 1000
}

const PROTOCOLS_URL_OPTIONS = [
    { value: 'https://protocolstg-h2dqhjaxd2gqaran.westeurope-01.azurewebsites.net/', text: 'Stage - https://protocolstg-h2dqhjaxd2gqaran.westeurope-01.azurewebsites.net/' },
    { value: 'https://protocolproduction-d0c9ahe8cpfecvay.westeurope-01.azurewebsites.net/', text: 'Production - https://protocolproduction-d0c9ahe8cpfecvay.westeurope-01.azurewebsites.net/' },
]

const IconSelect: FC<{ icons: any, onChange: any, givenSelectedIcon: any }> = ({ icons, onChange, givenSelectedIcon }) => {
    const [selectedIcon, setSelectedIcon] = useState<string | undefined>(givenSelectedIcon)
    const clickHandler = (iconOptions: any) => {
        setSelectedIcon(iconOptions.icon)
        onChange(iconOptions)
    }
    return (
        <IconsContainer>
            {
                icons.map((iconOptions: any) => (
                    <IconBg selected={selectedIcon == iconOptions.icon} color={iconOptions.iconColor} onClick={() => clickHandler(iconOptions)}>
                        <Icon name={iconOptions.icon} />
                    </IconBg>
                ))
            }
        </IconsContainer >
    )
}

const StyledUserConditional = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    margin: 8px 0;
    padding: 8px 16px;
    border: 1px solid ${theme.color.lightGrayBackground};
    border-radius: 10px;
    background: ${theme.color.whiteBackground};
`

const UserConditional = ({ checked, handleChange, text }: { checked: boolean, handleChange: () => void, text: string }) => {
    return (
      <StyledUserConditional>
          <Text>{text}</Text>
          <Switch checked={checked} onChange={handleChange} name={text} color="primary" />
      </StyledUserConditional>
    )
}
const CreateModuleModal: FC = () => {
    const { t } = useTranslation('budget')
    const { modulesStore, orgs, uiStore } = useStore()
    const open = modulesStore.moduleModalisOpen

    const types = [
        { value: ModuleEnum.THIRD, text: `${t`third`} - ${t`third_hint`}` },
        { value: ModuleEnum.DELIVERY, text: `${t`externalSystems`} - ${t`delivery_hint`}` },
        { value: ModuleEnum.KEHILANET, text: t`kehilanet` },
        { value: ModuleEnum.EXPRESS_106, text: t`express106` },
    ]

    const ProviderTypes = [{ value: ProviderEnum.MAOF, text: t`maof` }, { value: '', text: t`none` }, { value: ProviderEnum.MEKOME, text: t`mekome` }]
    const communityQrModule = modulesStore.communityModules.find((module) => module.type === ModuleEnum.ID_QR_CODE);

    const closeModal = () => {
        modulesStore.closeModuleModal()
        modulesStore.clearCurrent()
        clearAll()
    }

    const [selectedIcon, setSelectedIcon] = useState<string | undefined | null>(undefined)

    const onChangeIcon = (icon: any) => {
        setSelectedIcon(icon.icon)
        setButtonColor(icon.iconColor)
    }

    const [type, setType] = useState<null | ModuleEnum>(null)
    const [buttonColor, setButtonColor] = useState<string | null>('')
    const [data, setData] = useState<string | null>('')
    const [title, setTitle] = useState<string | null>(null)
    const [orgId, setOrgId] = useState<string | null>(null)
    const [enable, setEnable] = useState<boolean>(false)
    const [provider, setProvider] = useState(ProviderTypes[1].value)
    const [directorPermission, setDirectorPermission] = useState<boolean>(false)
    const [description, setDescription] = useState<string | null>(null)
    const [link, setLink] = useState<string | null>(null)
    const [iconText, setIconText] = useState<string | null>(null)
    const [editModuleText, setEditModuleText] = useState<string | null>('')
    const [share, setShare] = useState(false);
    const [shareEmail, setShareEmail] = useState<string | null>(null);
    const [information, setInformation] = useState<string>('')
    const isValidFields = useRef<Array<string>>([]);

    const handleChangeValidFields = ({ str, field }: { str: string, field: ValidFields}) => {
        const isField = isValidFields.current.some((item) => item === field)

        if (str.length > ValidLengthFields[field] && !isField) {
            isValidFields.current = [...isValidFields.current, field]
        }

        if ( isValidFields.current.length > 0  && str.length <= ValidLengthFields[field]) {
            const d = isValidFields.current.filter((item ) => item != field )
            isValidFields.current = d
        }
    }

    const handleChangeShareEmail = (event: React.ChangeEvent<HTMLInputElement>) => {
        setShareEmail(event.target.value);
        handleChangeValidFields({ str: event.target.value, field: ValidFields.shareEmail });
    }

    useEffect(() => {
        clearAll()
        if (modulesStore?.currentModule) {
            setTitle(modulesStore?.currentModule?.title)
            setDirectorPermission(modulesStore?.currentModule?.directorPermission)
            setDescription(modulesStore?.currentModule?.description)
            setLink(modulesStore.currentModule?.external_url)
            setIconText(modulesStore.currentModule?.icon_text)
            setButtonColor(modulesStore.currentModule?.icon_color)
            setType(modulesStore?.currentModule?.type)
            setSelectedIcon(modulesStore?.currentModule?.icon_name)
            setOrgId(modulesStore?.currentModule?.org_id)
            setProvider(modulesStore?.currentModule?.provider)
            setData(modulesStore?.currentModule?.data)
            setEnable(modulesStore?.currentModule?.enable)
            setShareEmail(modulesStore?.currentModule.shareEmail)
            setShare(!!modulesStore?.currentModule.share)

            if (modulesStore?.currentModule.information) {
                const parseInformation = JSON.parse(modulesStore?.currentModule?.information);

                setInformation(parseInformation[0])
            }

            const newModule = modulesStore?.currentModule?.module_id
            if (modulesStore?.currentModule.type === ModuleEnum.DELIVERY) {
                setEditModuleText(`${newModule ? t`editModule` : ''} ${t`externalSystems`}`)
            } else if (modulesStore?.currentModule.type === ModuleEnum.THIRD) {
                setEditModuleText(`${newModule ? t`editModule` : ''} ${t`third`}`)
            } else if (modulesStore?.currentModule.type === ModuleEnum.PORTAL) {
                setEditModuleText(`${newModule ? t`editModule` : t`app:add2`} ${t`portal`}`)
            } else if (modulesStore?.currentModule.type === ModuleEnum.SUPPORT) {
                setEditModuleText(`${newModule ? t`editModule` : t`app:add2`} ${t`serviceCall:services`}`)
            } else if (modulesStore?.currentModule.type === ModuleEnum.SLIKA) {
                setEditModuleText(`${newModule ? t`editModule` : t`app:add2`} ${t`slika`}`)
            } else if (modulesStore?.currentModule.type === ModuleEnum.PROTOCOLS) {
                setEditModuleText(`${newModule ? t`editModule` : t`app:add2`} ${t`protocols`}`)
            } else if (modulesStore?.currentModule.type === ModuleEnum.SERVICE_DESK) {
                setEditModuleText(`${newModule ? t`editModule` : t`app:add2`} ${t`serviceDesk`}`)
            }
        }

    }, [modulesStore.currentModule])

    useEffect(() => {
        if (type == ModuleEnum.EXPRESS_106 && !modulesStore?.currentModule) {
            setTitle('106 מועצה איזורית')
            setDescription('מוקד 106')
            setSelectedIcon('tzachy')
            setButtonColor('#d91c14')
            setData(`המועצה האזורית כאן בשבילך!
כדי לפתוח קריאה - יש ללחוץ על הכפתור עם הפלוס ממש כאן ולענות על כמה שאלות קצרות שייתארו את פנייתך.
על מנת שהמועצה תוכל לקבל את פנייתך
הינך מסכים/ה שנעביר אליה את הפרטים הבאים עם הלחיצה על הכפתור: שם פרטי ומשפחה, מספר טלפון נייד, ושם היישוב.
למקרים דחופים נא להתקשר ל8669*`)
            setInformation('106 מועצה איזורית')
        }
    }, [type])

    const clearAll = () => {
        setTitle(null)
        setDescription(null)
        setLink(null)
        setIconText(null)
        setButtonColor('')
        setData('')
        setType(null)
        setSelectedIcon(undefined)
        setOrgId(null)
        setProvider(null)
        setShareEmail(null)
        setInformation('')
    }
    const [disabled, setDisabled] = useState(false)

    const resolveProvider = () => {
        if (type === ModuleEnum.ID_QR_CODE)  {
            return ProviderEnum.MEKOME_QR
        } 

        if (type === ModuleEnum.KEHILANET) {
            return ProviderEnum.KEHILANET
        }

        if (type == ModuleEnum.EXPRESS_106) {
            return ProviderEnum.EXPRESS_106
        }

        if (type == ModuleEnum.PROTOCOLS) {
            return ProviderEnum.PROTOCOLS
        }

        if (type == ModuleEnum.SERVICE_DESK) {
            return ProviderEnum.SERVICE_DESK
        }

        return provider
    }

    const onSave = async () => {
        setDisabled(true)

        if (isValidFields.current.length) {
            const currentField = isValidFields.current[0]
            const length = ValidLengthFields[currentField as ValidFields]

            snackBar.showError(t('app:fieldTooLong', { field: t(currentField), length }))
            setDisabled(false)
            return
        }

        if (type != null) {
            const strInformation = JSON.stringify({ 0: information })
            const module: ModuleType = {
                community_ref: type === ModuleEnum.THIRD ? null : modulesStore?.currentModule?.community_ref || orgs.currentOrgId,
                module_id: modulesStore?.currentModule?.module_id || null,
                type: type, //(enum: portal, budget, third, reservations, voting) + nut null
                title: title, //string && nullable
                directorPermission: directorPermission, 
                description: description, //string && nullable
                external_url: link, //string && nullable
                icon_name: selectedIcon, //string && nullable
                icon_text: iconText,
                icon_color: buttonColor,
                org_id: orgId,
                enable: enable,
                provider: resolveProvider(),
                information: strInformation,
                data: data,
                share,
                shareEmail
            }

            if (modulesStore?.currentModule?.module_id) {
                await modulesStore.updateModule(module)
            } else {
                await modulesStore.addNewModule(module)
            }
            
            await modulesStore.getCommunityModulesAndFeatures(orgs.currentOrgId)
            setDisabled(false)
            closeModal()
        }
    }

    const onDelete = () => {
        let deleteText = ''
        switch (type) {
            case ModuleEnum.THIRD:
                deleteText = t`third`
                break;
            case ModuleEnum.DELIVERY:
                deleteText = t`externalSystems`
                break;
            case ModuleEnum.SLIKA:
                deleteText = t`slika`
                break;
            case ModuleEnum.SUPPORT:
                deleteText = t`serviceCall:services`
                break;
            case ModuleEnum.PORTAL:
                deleteText = t`portal`
                break;
            case ModuleEnum.ID_QR_CODE:
                deleteText = ModuleEnum.ID_QR_CODE
                break;
            case ModuleEnum.EXPRESS_106:
                deleteText = t`express106_council`
                break;
            case ModuleEnum.PROTOCOLS:
                deleteText = t`protocols`
                break;
            case ModuleEnum.SERVICE_DESK:
                deleteText = t`serviceDesk`
                break;
        }

        uiStore.openWarningModal({
            text: `${t`remove:moduleRemove`} ${deleteText}`,
            secondText: (modulesStore?.currentModule?.title && modulesStore?.currentModule?.title != '') ? `${t`remove:moduleName`} ${modulesStore?.currentModule?.title}` : '',
            okAction: () => {
                if (modulesStore?.currentModule) {
                    modulesStore.deleteModule(modulesStore?.currentModule?.module_id)
                } else if (communityQrModule) {
                    modulesStore.deleteModule(communityQrModule.module_id)
                }
                closeModal()
            }
        })

    }
    const canSave = () => {
        if (type === ModuleEnum.ID_QR_CODE) {
            return !!communityQrModule;
        }
        if (type === ModuleEnum.DELIVERY && title && link && validURL(link) && selectedIcon) {
            return false
        }
        if (type === ModuleEnum.EXPRESS_106 && link && validURL(link) && title && description && selectedIcon && buttonColor != '') {
            return false
        }
        if (type === ModuleEnum.THIRD && title && description  && iconText && buttonColor != '') {
            return false
        }
        if (type === ModuleEnum.PORTAL && link && validURL(link)) {
            return false
        }
        if (type === ModuleEnum.SUPPORT && link && validURL(link)) {
            return false
        }
        if (type === ModuleEnum.SLIKA && orgId) {
            return false
        }
        if (type === ModuleEnum.EMERGENCY_MANAGER && orgId) {
           return false
        }
        if (type === ModuleEnum.KEHILANET && orgId && link && validURL(link) ) {
           return false
        }
        if (type === ModuleEnum.PROTOCOLS && link) {
            return false
        }
        if (type === ModuleEnum.SERVICE_DESK && link) {
            return false
        }

        return true
    }

    const canCreateService = !modulesStore.communityModules?.find(el => el.type === ModuleEnum.SUPPORT)//true if theres no support
    const canCreateEmergencyManager = !modulesStore.communityModules?.find(el => el.type === ModuleEnum.EMERGENCY_MANAGER)
    const canCreateProtocol = !modulesStore.communityModules?.find(el => el.type === ModuleEnum.PROTOCOLS)
    const canCreateServiceDesk = !modulesStore.communityModules?.find(el => el.type === ModuleEnum.SERVICE_DESK)

    if (canCreateService) {
        types.push({ value: ModuleEnum.SUPPORT, text: t`serviceCall:services` })
    }

    if (canCreateEmergencyManager) {
        types.push({ value: ModuleEnum.EMERGENCY_MANAGER, text: t`emergency_manager` })
    }

    if (canCreateProtocol) {
        types.push({ value: ModuleEnum.PROTOCOLS, text: t`protocols` })
    }

    if (canCreateServiceDesk) {
        types.push({ value: ModuleEnum.SERVICE_DESK, text: t`serviceDesk` })
    }

    const handleEditorChange = ({ text }: { text: string }) => {
        setInformation(text)
        handleChangeValidFields({ str: text, field: ValidFields.information });
    }

    const handleDirectorPermission = (e: any) => {
        setDirectorPermission(e.target.checked)
    }

    return (
        <DialogContainer
            style={{ zIndex: 1200 }}
            open={open}
            onBackdropClick={closeModal}
        >
            <HeaderBar>
                <p className="header-title">{modulesStore?.currentModule ? editModuleText : t`addNewModule`}</p>
                <IconButton onClick={closeModal}>
                    <Close />
                </IconButton>
            </HeaderBar>
            <ContainerDialog>
              <Card>
              {userIsSuperAdmin() && <StyledUserConditional>
                <Text>{t`isEnable`}</Text>
                <Switch 
                  checked={enable} 
                  onChange={({ target }) => { setEnable(target.checked) }}  
                  color="primary" 
                />
              </StyledUserConditional>}
                {!modulesStore?.currentModule && <><Autocomplete
                    value={types.filter(i => i.value === type)[1]}
                    onChange={(event, value) => { 
                        setType(value?.value || null)
                        setDirectorPermission(false)

                        if(value?.value == ModuleEnum.EMERGENCY_MANAGER) {
                            setOrgId(orgs.currentOrgId)

                            const emergencyModule = modulesStore
                              .communityModules
                              .find(el => el.type === ModuleEnum.EMERGENCY_MANAGER)  
                      
                            if (emergencyModule !== undefined) {
                                setDirectorPermission(emergencyModule.directorPermission)
                            }  
                        } 

                        if(value?.value == ModuleEnum.EXPRESS_106) {
                            setOrgId(orgs.currentOrgId)

                            const express106Module = modulesStore
                              .communityModules
                              .find(el => el.type === ModuleEnum.EXPRESS_106)  
                      
                            if (express106Module !== undefined) {
                                modulesStore.setCurrentModule(express106Module.module_id)
                            }  
                        } 
                    }}
                    id="combo-box-demo"
                    options={types}
                    placeholder={t`type`}
                    getOptionLabel={(option) => option.text}
                    style={{ width: '100%' }}
                    renderInput={(params) => <TextField {...params} value={type} label={t`type`} variant="outlined" />}
                /><Space height={1} /></>}

                {type && type !== ModuleEnum.ID_QR_CODE && <>
                    {(type === ModuleEnum.PORTAL || type === ModuleEnum.SUPPORT) && <><Autocomplete
                        value={ProviderTypes.filter(i => i.value === provider)[0]}
                        onChange={(event, value) => { setProvider(value?.value || null) }}
                        id="combo-box-provider"
                        options={ProviderTypes}
                        placeholder={t`provider`}
                        getOptionLabel={(option) => option.text}
                        style={{ width: '100%' }}
                        renderInput={(params) => <TextField {...params} value={type} label={t`provider`} variant="outlined" />}
                    /> <Space height={1} /></>}
                   
                    {(type === ModuleEnum.KEHILANET || type === ModuleEnum.SLIKA || type === ModuleEnum.PORTAL || type === ModuleEnum.SUPPORT) && provider != ProviderEnum.MEKOME && <StyledTextField
                        value={orgId}
                        id="outlined-basic"
                        label={t`orgId`} variant="outlined"
                        onChange={(event) => setOrgId(event.target.value)}
                    />}
                    {type != ModuleEnum.PROTOCOLS && type != ModuleEnum.SERVICE_DESK && type != ModuleEnum.PORTAL && type != ModuleEnum.EMERGENCY_MANAGER && type != ModuleEnum.SUPPORT && type != ModuleEnum.SLIKA && (
                      <><Space height={1} />
                      <StyledTextField
                        value={title}
                        id="outlined-basic"
                        label={type == ModuleEnum.EXPRESS_106 ? t`express106_button_title` : t`title`} variant="outlined"
                        onChange={(event) => setTitle(event.target.value)}
                      /></>
                      )
                    }

                    {(type == ModuleEnum.EXPRESS_106)  &&
                      <>
                        <Space height={1} />
                        <StyledTextField
                          value={description}
                          id="outlined-basic"
                          label={t`subtitle`}
                          variant="outlined"
                          onChange={(event) => setDescription(event.target.value)}
                        />
                      </>
                    }

                    {(type === ModuleEnum.THIRD || type === ModuleEnum.KEHILANET)  &&
                        <>
                            <Space width={1} />
                            {description ? <Text
                                style={{ alignSelf: 'flex-start' }}
                                textcolor={theme.color.black + 99}
                                weight={'normal'}
                                size={'xs'}
                            >{t`description`}</Text> : <div style={{ height: 18 }} />}
                            <StyledTextarea
                                rowsMin={4}
                                defaultValue={description}
                                placeholder={t`description`}
                                onChange={(event) => setDescription(event.target.value)}
                            />
                        </>
                    }

                    {type === ModuleEnum.KEHILANET &&
                      <>
                        <Space height={1} />
                        <Autocomplete
                          onChange={(event, value) => {
                            const baseUrl = 'https://www.kehilanet.co.il/mekome/login.asp'
                            setLink(`${baseUrl}?interfaceId=${value?.value}`)
                          }}
                          id="combo-box-provider"
                          options={kehilanetData.interfaces}
                          placeholder={t`kehilanet_module`}
                          getOptionLabel={(option) => option.text}
                          style={{width: '100%'}}
                          renderInput={(params) => <TextField {...params} label={t`kehilanet_module`} variant="outlined"/>}
                        />
                      </>
                    }
                    
                    {type === ModuleEnum.PROTOCOLS &&
                      <>
                        <Space height={1} />
                        <Autocomplete
                          value={PROTOCOLS_URL_OPTIONS.find(o => o.value === link) || null}
                          onChange={(event, value) => setLink(value?.value || null)}
                          id="combo-box-protocols-url"
                          options={PROTOCOLS_URL_OPTIONS}
                          placeholder={t`url`}
                          getOptionLabel={(option) => option.text}
                          style={{ width: '100%' }}
                          renderInput={(params) => <TextField {...params} label={t`url`} variant="outlined" />}
                        />
                      </>
                    }

                    {type === ModuleEnum.SERVICE_DESK &&
                      <>
                        <Space height={1} />
                        <Autocomplete
                          value={SERVICE_DESK_URL_OPTIONS.find(o => o.value === link) || null}
                          onChange={(event, value) => setLink(value?.value || null)}
                          id="combo-box-servicedesk-url"
                          options={SERVICE_DESK_URL_OPTIONS}
                          placeholder={t`url`}
                          getOptionLabel={(option) => option.text}
                          style={{ width: '100%' }}
                          renderInput={(params) => <TextField {...params} label={t`url`} variant="outlined" />}
                        />
                      </>
                    }

                    {type != ModuleEnum.KEHILANET && type != ModuleEnum.SLIKA && type != ModuleEnum.EMERGENCY_MANAGER && type != ModuleEnum.PROTOCOLS && type != ModuleEnum.SERVICE_DESK && <><Space height={1} /><StyledTextField
                        value={link}
                        inputProps={{ style: { direction: 'ltr', textAlign: 'left' } }}
                        id="outlined-basic"
                        label={type == ModuleEnum.EXPRESS_106 ? t`link_template` : t`url`}
                        variant="outlined"
                        onChange={(event) => setLink(event.target.value)}
                    /></>}

                    {(type == ModuleEnum.EXPRESS_106)  &&
                      <>
                        <Space width={1} />
                        {data ? <Text
                            style={{ alignSelf: 'flex-start' }}
                            textcolor={theme.color.black + 99}
                            weight={'normal'}
                            size={'xs'}
                        >{t`opening_text`}</Text> : <div style={{ height: 18 }} />}
                        <StyledTextarea
                            rowsMin={4}
                            defaultValue={data}
                            placeholder={t`opening_text`}
                            onChange={(event) => setData(event.target.value)}
                        />
                        <Space height={1} />
                        <StyledTextField
                          value={information}
                          id="outlined-basic"
                          label={t`top_subtitle`}
                          variant="outlined"
                          onChange={(event) => setInformation(event.target.value)}
                        />
                      </>
                    }
                   
                    {/* {type === 'third' && <Row style={{ alignItems: 'flex-start', }}> */}
                    {(type === ModuleEnum.THIRD) &&  <><Space height={1} /><StyledTextField
                        value={iconText}
                        id="outlined-basic"
                        label={t`iconText`}
                        variant="outlined"
                        onChange={(event) => setIconText(event.target.value)}
                    /></>}
                   
                    {type != ModuleEnum.KEHILANET && type != ModuleEnum.PORTAL && type != ModuleEnum.SUPPORT && type != ModuleEnum.SLIKA && type != ModuleEnum.EMERGENCY_MANAGER && type != ModuleEnum.PROTOCOLS && type != ModuleEnum.SERVICE_DESK &&  <><Space width={1} /><StyledTextField
                        style={{ direction: 'ltr', textAlign: buttonColor === '' ? 'end' : 'start' }}
                        value={buttonColor}
                        id="outlined-basic"
                        label={type === 'third' ? t`buttonColor` : t`iconColor`}
                        variant="outlined"
                        onChange={(event) => setButtonColor(event.target.value)}
                    /></>}
                    {type != ModuleEnum.EXPRESS_106 && type != ModuleEnum.KEHILANET && type != ModuleEnum.PORTAL && type != ModuleEnum.SUPPORT && type != ModuleEnum.SLIKA && type != ModuleEnum.EMERGENCY_MANAGER && type != ModuleEnum.PROTOCOLS && type != ModuleEnum.SERVICE_DESK && (
                      <>
                        <Space width={1} />
                        <StyledTextField
                          value={shareEmail || ''}
                          inputProps={{ style: { direction: 'ltr', textAlign: 'left' } }}
                          id="outlined-basic"
                          label={t`shareEmail`}
                          variant="outlined"
                          name="shareEmail"
                          onChange={handleChangeShareEmail}
                        />
                        <Space width={1} />
                        <RichTextEditor
                            style={{width: '100%', minHeight: 200}}
                            value={information}
                            placeholder={t`information`}
                            onChange={handleEditorChange}
                        />
                        <Space width={1} />
                        <UserConditional checked={share} text={t`share`} handleChange={() => setShare(prevState => !prevState)} />
                      </>
                    )}

                    {(type == ModuleEnum.EXPRESS_106 || type === ModuleEnum.DELIVERY || type == ModuleEnum.KEHILANET) &&  (<><Space width={1} /><Accordion>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                            <SelectContent>
                                <Space width={1} />
                                {selectedIcon && <IconBg
                                    selected={false}
                                    color={buttonColor}
                                >
                                    <Icon name={selectedIcon} />
                                </IconBg>}
                                <Text>{selectedIcon ? t`switchIcon` : t`icon`}</Text>
                            </SelectContent>
                        </AccordionSummary>
                        <AccordionDetails>
                            <IconSelect
                                icons={icons}
                                onChange={onChangeIcon}
                                givenSelectedIcon={selectedIcon} />
                        </AccordionDetails>
                    </Accordion></>
                    )}

                    {type === ModuleEnum.EMERGENCY_MANAGER && (
                      <>
                        <Space height={1} />
                        <CheckBoxContainer>
                          <StyledCheckBox
                            checked={directorPermission}
                            onChange={handleDirectorPermission}
                          />
                          <Text textcolor={theme.color.black} weight={'normal'} size={'sm'}>
                            {t`allow_director_to_manage`}
                          </Text>
                        </CheckBoxContainer>
                    </>
                    )}
                </>}
              </Card>
            </ContainerDialog>
            <FooterBar>
                {!!modulesStore?.currentModule || (type === ModuleEnum.ID_QR_CODE && !!communityQrModule) ? <DeleteButton onClick={onDelete}>{t`common:deletion`}</DeleteButton> : <div />}
                <ConfirmButton disabled={canSave() || disabled} onClick={onSave}>{t`save`}</ConfirmButton>
            </FooterBar>

        </DialogContainer>
    )
}

export default observer(CreateModuleModal)


